import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from '@/shared/services/userService';
import { isTokenExpired } from '@/shared/utils/authUtils';
import type { AuthState, User } from '@/shared/types';

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
  isAuthenticated: false,
  isInitialized: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await userService.login(email, password);

      localStorage.setItem('token', response.access_token);
      if (response.refresh_token) {
        localStorage.setItem('refresh_token', response.refresh_token);
      }

      console.log('[Login] Success. Response:', response);

      if (response.userId) {
        const user: User = {
          id: response.userId,
          name: response.userFirstName,
          email: email,
          createdAt: new Date().toISOString(),
        };

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userId', response.userId.toString());
        localStorage.setItem('userEmail', email);
        console.log('[Login] Stored userId:', response.userId);

        return {
          ...response,
          user: user,
        };
      } else {
        console.error('[Login] Response missing userId:', response);
        return rejectWithValue('Erro no login: Dados do usuário inválidos.');
      }
    } catch (error) {
      console.error('Login error:', error);
      if ((error as any).response?.data?.message) {
        return rejectWithValue((error as any).response.data.message);
      }
      return rejectWithValue('Erro ao fazer login. Verifique suas credenciais.');
    }
  },
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: any, { rejectWithValue }) => {
    try {
      const user = await userService.createUser(userData);
      return user;
    } catch (error) {
      return rejectWithValue((error as any).response?.data?.message || 'Erro ao criar conta');
    }
  },
);

export const requestPasswordRecovery = createAsyncThunk(
  'auth/requestPasswordRecovery',
  async (email: string, { rejectWithValue }) => {
    try {
      await userService.requestPasswordRecovery(email);
      return true;
    } catch (error) {
      return rejectWithValue(
        (error as any).response?.data?.message || 'Erro ao solicitar recuperação de senha',
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    { email, token, password }: { email: string; token: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      await userService.resetPassword(email, token, password);
      return true;
    } catch (error) {
      return rejectWithValue((error as any).response?.data?.message || 'Erro ao redefinir senha');
    }
  },
);

export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, { rejectWithValue }) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');

  if (!token) {
    return rejectWithValue('No token found');
  }

  try {
    let user: User | null = null;
    if (userStr) {
      try {
        user = JSON.parse(userStr);
      } catch {
        // invalid user data
      }
    }

    if (!user || !user.id) {
      return rejectWithValue('Token exists but user data is missing or invalid');
    }

    if (isTokenExpired(token)) {
      return rejectWithValue('Token expired');
    }

    return { token, user };
  } catch (_) {
    return rejectWithValue('Authentication check failed');
  }
});

// ============================================================================
// Slice
// ============================================================================

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;

      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.user = payload.user;
        state.token = payload.access_token;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Password Recovery
    builder
      .addCase(requestPasswordRecovery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPasswordRecovery.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestPasswordRecovery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        if (action.payload.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = null;

        localStorage.removeItem('token');
      });
  },
});

export const { logout, setUser, clearError } = authSlice.actions;
export default authSlice.reducer;
